import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAdminBanners,
  createBanner,
  updateBanner,
  deleteBanner,
  uploadImage,
} from '@/api/contentService';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { 
  Bell, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  EyeOff,
  Upload
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { optimizeBannerImage } from '@/lib/imageCompression';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AdminBanners() {
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link: '',
    signup_enabled: false,
    signup_link: '',
    expiration_date: '',
    is_active: true,
    order: 0
  });

  const queryClient = useQueryClient();

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['adminBanners'],
    queryFn: () => listAdminBanners(50)
  });

  const createMutation = useMutation({
    mutationFn: (data) => createBanner(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBanners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      queryClient.invalidateQueries({ queryKey: ['allBanners'] });
      toast.success('Banner criado com sucesso!');
      resetForm();
    },
    onError: (error) => {
      toast.error(error?.message || 'Erro ao criar banner.');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateBanner(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBanners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      queryClient.invalidateQueries({ queryKey: ['allBanners'] });
      toast.success('Banner atualizado!');
      resetForm();
    },
    onError: (error) => {
      toast.error(error?.message || 'Erro ao atualizar banner.');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteBanner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBanners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      queryClient.invalidateQueries({ queryKey: ['allBanners'] });
      toast.success('Banner excluído!');
    },
    onError: (error) => {
      toast.error(error?.message || 'Erro ao excluir banner.');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      link: '',
      signup_enabled: false,
      signup_link: '',
      expiration_date: '',
      is_active: true,
      order: 0
    });
    setEditingBanner(null);
    setShowForm(false);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      image_url: banner.image_url || '',
      link: banner.link || '',
      signup_enabled: banner.signup_enabled || false,
      signup_link: banner.signup_link || '',
      expiration_date: banner.expiration_date || '',
      is_active: banner.is_active ?? true,
      order: banner.order || 0
    });
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const optimizedFile = await optimizeBannerImage(file);
      const fileUrl = await uploadImage(optimizedFile, 'banners');
      setFormData(prev => ({ ...prev, image_url: fileUrl }));
      toast.success('Imagem enviada em formato otimizado!');
    } catch (error) {
      toast.error(error?.message || 'Erro ao enviar imagem.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.image_url) {
      toast.error('Título e imagem são obrigatórios.');
      return;
    }

    if (editingBanner) {
      updateMutation.mutate({ id: editingBanner.id, data: formData });
    } else {
      createMutation.mutate({ ...formData, order: formData.order || banners.length + 1 });
    }
  };

  const toggleActive = (banner) => {
    updateMutation.mutate({ 
      id: banner.id, 
      data: { is_active: !banner.is_active } 
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Banners" 
            subtitle="Avisos da home"
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {banners.length === 0 ? (
          <EmptyState
            icon={Bell}
            title="Nenhum banner cadastrado"
            description="Crie banners para exibir na home."
            actionLabel="Criar Banner"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {banners.map((banner, index) => (
              <motion.div
                key={banner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-xl shadow-sm overflow-hidden ${!banner.is_active ? 'opacity-60' : ''}`}
              >
                {banner.image_url && (
                  <div className="aspect-video relative">
                    <img
                      src={banner.image_url}
                      alt={banner.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    {!banner.is_active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-white px-3 py-1 rounded-full text-sm font-medium">
                          Inativo
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900">{banner.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">Posição: {Number(banner.order || 0)}</p>
                  {banner.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{banner.description}</p>
                  )}
                  {banner.signup_enabled && (
                    <p className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1 mt-3 inline-flex">
                      Botão de inscrição ativo
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleActive(banner)}
                        className="p-2 rounded-lg hover:bg-gray-100"
                      >
                        {banner.is_active ? (
                          <Eye className="w-4 h-4 text-green-600" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(banner)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200"
                        onClick={() => {
                          if (confirm('Excluir este banner?')) {
                            deleteMutation.mutate(banner.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Banner Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBanner ? 'Editar Banner' : 'Novo Banner'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Título do banner"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição curta"
                rows={2}
              />
            </div>

            <div>
              <Label>Imagem *</Label>
              <p className="text-xs text-gray-500 mt-1">
                A imagem sera comprimida automaticamente para carregar mais leve no app e economizar trafego.
              </p>
              {formData.image_url ? (
                <div className="relative mt-2">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded-lg"
                    loading="lazy"
                    decoding="async"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-500 mt-2">Clique para enviar</span>
                    </>
                  )}
                </label>
              )}
            </div>

            <div>
              <Label>Link (opcional)</Label>
              <Input
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Botão "Faça sua inscrição"</Label>
                  <p className="text-xs text-gray-500 mt-1">
                    Quando ativado, aparece sobre este banner.
                  </p>
                </div>
                <Switch
                  checked={formData.signup_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, signup_enabled: checked })}
                />
              </div>

              {formData.signup_enabled && (
                <div>
                  <Label>Link da inscrição (opcional)</Label>
                  <Input
                    value={formData.signup_link}
                    onChange={(e) => setFormData({ ...formData, signup_link: e.target.value })}
                    placeholder="/inscricao-evento?evento=acampamento-mbv-2026"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se ficar vazio, abre a página pública de inscrição. Evite links com 127.0.0.1 ou localhost.
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label>Data de expiração (opcional)</Label>
              <Input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Ordem do banner</Label>
              <Input
                type="number"
                min="1"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                placeholder="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use 1 para aparecer primeiro, 2 para segundo, e assim por diante.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label>Banner ativo</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingBanner ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
